import { endOfDay, endOfMonth, startOfDay, startOfMonth, subDays, subMonths } from 'date-fns'
import UDSTLogo from '../images/TetherLogo.svg'
import USDCLogo from '../images/USDCLogo.svg'
import EthLogo from '../images/EthLogo.svg'
import ArbitrumLogo from '../images/ArbitrumLogo.svg'
import PolygonLogo from '../images/PolygonLogo.svg'
import TronLogo from '../images/TronLogo.svg'
import SolLogo from '../images/SolLogo.png'
import BaseLogo from '../images/BaseLogo.svg'

export const STAGE = process.env.STAGE ?? 'local'
export const IS_LOCAL = STAGE === 'local'
export const IS_DEV = STAGE === 'dev'
export const IS_TEST = STAGE === 'test'
export const IS_PROD = STAGE === 'prod'

const API_BASE_URLS = {
  local: 'http://localhost:3000',
  // local: 'https://api.radom.com',
  dev: 'https://api.dev.radom.com',
  test: 'https://api.test.radom.com',
  prod: 'https://api.radom.com'
}

const PAY_BASE_URLS = {
  local: 'http://localhost:1235',
  // local: 'https://pay.radom.com',
  dev: 'https://pay.dev.radom.com',
  test: 'https://pay.test.radom.com',
  prod: 'https://pay.radom.com'
}

export const API_BASE_URL = API_BASE_URLS[STAGE]
export const PAY_BASE_URL = PAY_BASE_URLS[STAGE]

export enum RADOM_COLORS {
  BLACK = '#282828',
  BLUE = 'rgb(7, 7, 40)',
  NEW_BLUE = '#2963ff',
  BLUE_ALT = '#0E259C',
  BLUE_MED = '#0F1D4B',
  PURPLE = '#9F76EA',
  ORANGE = '#FF6648',
  ORANGE_MED = '#FF8972',
  ORANGE_LIGHT = '#FFDBD4',
  ORANGE_LIGHTEST = '#FFF2EFAA',
  SUCCESS = '#36AF67',
  SUCCESS_LIGHT = '#d8f3e3',
  WARNING = '#F79009',
  WARNING_LIGHT = '#FEF0C7',
  INFO = '#5490EC',
  INFO_LIGHT = '#d1e1fa',
  ERROR = '#FF5252',
  ERROR_LIGHT = '#ffdfdf',
  GRAY_DARKEST = '#636576',
  GRAY_DARKER = '#9B9EB1',
  GRAY_DARK = '#E2E4EF',
  GRAY_MED = '#EFF0F8',
  GRAY_LIGHTEST = '#F8F8FB',
  INPUT_PLACEHOLDER = '#9ba3af',
  NICE_GREEN = '#4bb69c',
  NICE_BLUE = '#07053f',
  NEW_GRAY_1 = '#33324F',
  DARK_BLUE = '#07053f',
  BLUE4 = '#3464e6',
  BLUE5 = '#618afa',
  BLUE7 = '#e7ecff',
  GRAY4 = '#8D91AC',
  GRAY8 = '#E3E5F0',
  GRAY9 = '#eff0f8',
  RED50 = '#ffecf0',
  RED300 = '#ffa9b9',
  RED900 = '#dc082e',
  INPUT_DEFAULT = 'rgb(239 240 248 / 60%)',
  WHITE = '#ffffff',
  RADOM_BLUE = '#2A63FF',
  RADOM_BLUE_LIGHTER = '#7fa1ffff',
  RADOM_BLUE_LIGHTEST = '#8faaf4ff'
}

export enum WINDOW_SIZE {
  MOBILE_SMALL = '320px',
  MOBILE_MEDIUM = '375px',
  MOBILE_LARGE = '425px',
  TABLET = '768px',
  LAPTOP = '1024px',
  LAPTOP_LARGE = '1440px',
  DESKTOP = '2560px'
}

export const RADOM_TOKEN_LOCAL_STORAGE_KEY = 'radomToken'
export const RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY = 'radomTokenExpiry'

export const EXPLORER_URLS: { [key: string]: string } = {
  RadomDevNet: 'http://localhost:9545',
  GoerliTestnet: 'https://goerli.etherscan.io',
  PolygonTestnet: 'https://mumbai.polygonscan.com',
  FantomTestnet: 'https://testnet.ftmscan.com',
  SepoliaTestnet: 'https://sepolia.etherscan.io',
  BitcoinTestnet: 'https://mempool.space/testnet',
  Bitcoin: 'https://mempool.space',
  AuroraTestnet: 'https://explorer.testnet.aurora.dev',
  Aurora: 'https://explorer.aurora.dev',
  Arbitrum: 'https://arbiscan.io',
  Ethereum: 'https://etherscan.io',
  Fantom: 'https://ftmscan.com',
  AvalancheTestnet: 'https://testnet.snowtrace.io',
  Avalanche: 'https://snowtrace.io',
  ScrollTestnet: 'https://blockscout.scroll.io',
  Polygon: 'https://polygonscan.com',
  BNB: 'https://bscscan.com',
  BNBTestnet: 'https://testnet.bscscan.com',
  PolkadotTestnet: 'https://westend.subscan.io',
  Polkadot: 'https://polkadot.subscan.io',
  TronTestnet: 'https://nile.tronscan.org/#',
  Tron: 'https://tronscan.org/#',
  Base: 'https://basescan.org',
  Solana: 'https://solscan.io',
  SolanaDevnet: 'https://solscan.io',
  PolkadotAssetHub: 'https://assethub-polkadot.subscan.io',
  PolkadotAssetHubTestnet: 'https://assethub-westend.subscan.io'
}

export const SANCTIONED_COUNTRIES = new Set([
  'Afghanistan',
  'Belarus',
  'Central African Republic',
  'Cuba',
  'Democratic Republic of the Congo',
  'Ethiopia',
  'Iran',
  'Iraq',
  'Lebanon',
  'Libya',
  'Mali',
  'Myanmar',
  'Nicaragua',
  'North Korea',
  'Russia',
  'Somalia',
  'South Sudan',
  'Sudan',
  'Syria',
  'Ukraine',
  'Venezuela',
  'Yemen'
])

export const ZERO_POPULATION_TERRITORIES = new Set([
  'Antarctica',
  'Baker Island',
  'Bouvet Island',
  'Heard Island and McDonald Islands',
  'Howland Island',
  'Jarvis Island',
  'Johnston Atoll',
  'Kingman Reef',
  'Palmyra Atoll',
  'Paracel Islands',
  'South Georgia'
])

export enum EXPORT_COLUMN_TYPES {
  ALL_COLUMNS = 'All columns',
  CUSTOM = 'Custom'
}

export enum EXPORT_PAYEMNTS_TYPE {
  ALL_PAYMENTS = 'All Payments',
  OPEN_BANKING_PAYMENTS = 'Open Banking Payments',
}

export const EXPORT_PAYMENTS_COLUMNS: Record<string, { [key: string]: { label: String, unchecked?: boolean } }> = {
  [EXPORT_PAYEMNTS_TYPE.ALL_PAYMENTS]: {
    Id: { label: 'ID' },
    Date: { label: 'Date' },
    Currency: { label: 'Currency' },
    Subtotal: { label: 'Subtotal' },
    Discount: { label: 'Discount' },
    AddedNetworkFee: { label: 'Added network fee' },
    Total: { label: 'Total' },
    ConvertedCurrency: { label: 'Converted currency' },
    ConvertedTotal: { label: 'Converted total' },
    Network: { label: 'Network' },
    Token: { label: 'Token' },
    GrossPayment: { label: 'Gross payment' },
    NetworkFee: { label: 'Network fee' },
    RadomFee: { label: 'Radom fee' },
    TotalFee: { label: 'Total fee' },
    NetAmount: { label: 'Net amount' },
    RefundedAmount: { label: 'Refunded amount' },
    ConvertedGrossPayment: { label: 'Converted gross payment' },
    ConvertedNetworkFee: { label: 'Converted network fee' },
    ConvertedRadomFee: { label: 'Converted radom fee' },
    ConvertedTotalFee: { label: 'Converted total fee' },
    ConvertedNetAmount: { label: 'Converted net amount' },
    ConvertedRefundedAmount: { label: 'Converted refunded amount' },
    SenderAddress: { label: 'Sender address' },
    TransactionHash: { label: 'Transaction hash' },
    BalanceBefore: { label: 'Balance before' },
    BalanceAfter: { label: 'Balance after' }
  },
  [EXPORT_PAYEMNTS_TYPE.OPEN_BANKING_PAYMENTS]: {
    Id: { label: 'ID' },
    CheckoutSessionId: { label: 'Checkout Session Id' },
    BankStatementReference: { label: 'Bank Statement Reference' },
    Email: { label: 'Email' },
    Date: { label: 'Date' },
    Currency: { label: 'Currency' },
    Total: { label: 'Total' },
    OnrampGrossAmount: { label: 'Onramp Gross Amount' },
    OnrampNetAmount: { label: 'Onramp Net Amount' },
    OnrampNetwork: { label: 'Onramp Network' },
    OnrampToken: { label: 'Onramp Token' },
    OnrampFee: { label: 'Onramp Fee' },
    OnrampStatus: { label: 'Onramp Status' },
    BalanceBefore: { label: 'Balance before' },
    BalanceAfter: { label: 'Balance after' }
  }
}

export const EXPORT_WITHDRAWALS_COLUMNS: Record<string, { label: string, unchecked?: boolean }> = {
  Id: { label: 'Id' },
  Date: { label: 'Date' },
  Network: { label: 'Network' },
  Token: { label: 'Token' },
  Ticker: { label: 'Ticker' },
  RequestedAmount: { label: 'Requested amount' },
  FeeAmount: { label: 'Fee amount' },
  NetAmount: { label: 'Net amount' },
  TransactionHash: { label: 'Transaction hash' },
  WithdrawalAddress: { label: 'Withdrawal address' },
  BalanceBefore: { label: 'Balance before' },
  BalanceAfter: { label: 'Balance after' }
}

export const EXPORT_DEPOSITS_COLUMNS: Record<string, { label: string, unchecked?: boolean }> = {
  Id: { label: 'Id' },
  Date: { label: 'Date' },
  Currency: { label: 'Currency' },
  Total: { label: 'Total' },
  ConvertedCurrency: { label: 'Converted currency' },
  ConvertedTotal: { label: 'Converted total' },
  Network: { label: 'Network' },
  Token: { label: 'Token' },
  ConvertedTotalFee: { label: 'Converted total fee' },
  ConvertedNetAmount: { label: 'Converted net amount' },
  DepositAccountId: { label: 'Deposit account ID' },
  BalanceBefore: { label: 'Balance before' },
  BalanceAfter: { label: 'Balance after' }
}

export const EXPORT_PAYOUTS_COLUMNS: Record<string, { label: string, unchecked?: boolean }> = {
  Id: { label: 'Id' },
  Status: { label: 'Status' },
  ReceipientsAmount: { label: 'Receipients amount' },
  TotalPayoutUsd: { label: 'Total payout (USD)' },
  FeeUsd: { label: 'Fee (USD)' },
  CreatedAt: { label: 'Created at' },
  CompletedAt: { label: 'Completed at' },
  ExpiresAt: { label: 'Expires at' },
  DeductFees: { label: 'Deduct fees' },
  BalanceBefore: { label: 'Balance before' },
  BalanceAfter: { label: 'Balance after' }
}

export const EXPORT_TIME_RANGES = [
  {
    label: 'Today',
    dateFn: () => {
      return {
        from: startOfDay(new Date()),
        to: new Date()
      }
    }
  },
  {
    label: 'Current month',
    dateFn: () => {
      return {
        from: startOfMonth(new Date()),
        to: endOfDay(new Date())
      }
    }
  },
  {
    label: 'Last 7 days',
    dateFn: () => {
      return {
        from: startOfDay(subDays(new Date(), 7)),
        to: endOfDay(new Date())
      }
    }
  },
  {
    label: 'Last month',
    dateFn: () => {
      return {
        from: startOfMonth(subMonths(new Date(), 1)),
        to: endOfMonth(subMonths(new Date(), 1))
      }
    }
  },
  {
    label: 'All',
    dateFn: () => {
      return {
        from: new Date(0),
        to: new Date()
      }
    }
  },
  { label: 'Custom' }
]

export enum FiatAccountType {
  USD = 'USD',
  EUR = 'EUR',
  MXN = 'MXN',
  BRL = 'BRL'
}

export const FIAT_ACCOUNT_FLAGS = {
  [FiatAccountType.USD]: 'us',
  [FiatAccountType.EUR]: 'eu',
  [FiatAccountType.MXN]: 'mx',
  [FiatAccountType.BRL]: 'br'
}

export enum FiatDestinationCurrency {
  USDC = 'USDC',
  USDT = 'USDT',
}

export const FIAT_DESTINATION_CURRENCY_IMG = {
  [FiatDestinationCurrency.USDC]: USDCLogo as string,
  [FiatDestinationCurrency.USDT]: UDSTLogo as string
}

export enum FiatDestinationNetwork {
  Arbitrum = 'Arbitrum',
  Base = 'Base',
  Ethereum = 'Ethereum',
  Polygon = 'Polygon',
  Solana = 'Solana',
  Tron = 'Tron'
}

export const FIAT_PAYMENT_RAIL_IMG = {
  [FiatDestinationNetwork.Arbitrum]: ArbitrumLogo as string,
  [FiatDestinationNetwork.Base]: BaseLogo as string,
  [FiatDestinationNetwork.Ethereum]: EthLogo as string,
  [FiatDestinationNetwork.Polygon]: PolygonLogo as string,
  [FiatDestinationNetwork.Solana]: SolLogo,
  [FiatDestinationNetwork.Tron]: TronLogo as string
}

export const FIAT_CURRIENCES_FOR_NETWORK = {
  [FiatDestinationNetwork.Arbitrum]: [FiatDestinationCurrency.USDC],
  [FiatDestinationNetwork.Base]: [FiatDestinationCurrency.USDC],
  [FiatDestinationNetwork.Ethereum]: [FiatDestinationCurrency.USDC, FiatDestinationCurrency.USDT],
  [FiatDestinationNetwork.Polygon]: [FiatDestinationCurrency.USDC],
  [FiatDestinationNetwork.Solana]: [FiatDestinationCurrency.USDC, FiatDestinationCurrency.USDT],
  [FiatDestinationNetwork.Tron]: [FiatDestinationCurrency.USDT]
}

export const FIAT_CURRENCIES_FOR_ACCOUNT_TYPE = {
  [FiatAccountType.MXN]: [FiatDestinationCurrency.USDC],
  [FiatAccountType.BRL]: [FiatDestinationCurrency.USDC],
  [FiatAccountType.EUR]: [FiatDestinationCurrency.USDC],
  [FiatAccountType.USD]: [FiatDestinationCurrency.USDC, FiatDestinationCurrency.USDT]
}
